/**
 * Hidden route (/onboarding/preview) for re-opening the onboarding wizard
 * outside the signup flow. Intended for testing and demo purposes.
 *
 * - Requires an already-authenticated session (redirects to /login otherwise).
 * - Skips account activation and profile creation.
 * - Steps are determined by the authenticated user's role:
 *     ADMIN  -> Setup, Try Agentic DAM, Get Started
 *     Others -> Get Started (video guides + resource links)
 */
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';
import { useHistory, useLocation } from 'react-router-dom';
import Intro from './components/Intro';
import SetupStep from './steps/SetupStep';
import DemoMode from './steps/DemoMode';
import JumpOffGuide from './steps/JumpOffGuide';
import { DEFAULT_STATE, OnboardingActionType, OnboardingContext, OnboardingStep, reducer } from './context';

function buildPreviewSteps(roleName: string): OnboardingStep[] {
  const isUser = roleName === 'USER';

  return [
    ...(!isUser
      ? [
          { title: 'Setup', component: <SetupStep /> },
          { title: 'Try Agentic DAM', component: <DemoMode />, skippable: true },
          { title: 'Get Started', component: <JumpOffGuide /> },
        ]
      : [
          { title: 'Get Started', component: <JumpOffGuide /> },
        ]),
  ];
}

const OnboardingPreview = () => {
  const history = useHistory();
  const location = useLocation();
  const auth = useAuthUser();
  const authenticatedUser = auth();

  const role = authenticatedUser?.role || 'USER';

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const industryHint = queryParams.get('industry') || undefined;

  const steps = useMemo(() => buildPreviewSteps(role), [role]);

  const [state, dispatcher] = useReducer(reducer, {
    ...DEFAULT_STATE,
    steps,
    role,
    industryHint,
    userEmail: authenticatedUser?.userEmail,
  });

  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (state.finishedOnboarding) {
      history.push('/');
    }
  }, [state, history]);

  if (!authenticatedUser) {
    history.push('/login');
    return null;
  }

  const step = state.steps[state.currentStep - 1];

  return (
    <OnboardingContext.Provider value={[state, dispatcher]}>
      {showOnboarding ? step?.component : <Intro onStartOnboarding={() => setShowOnboarding(true)} />}
    </OnboardingContext.Provider>
  );
};

export default OnboardingPreview;
